import { put, select } from 'redux-saga/effects';
import moment from 'moment';
import { isEmpty, keys, mergeDeepRight, sort } from 'ramda';
import { ATTACHMENT, MESSAGE } from '../constants/AppConstants';
import { finishExport, sendExportToInbox, startExport } from '../actions/pdfActions';
import {
  SEND_TO_INBOX_SENDING_TOAST_PREFIX,
  SEND_TO_INBOX_SENDING_TOAST_SINGULAR_ITEM,
  SEND_TO_INBOX_SENDING_TOAST_PLURAL_ITEM,
  SEND_TO_INBOX_SENDING_TOAST_SUFFIX,
} from '../constants/StringConstants';
import PdfBuilder from '../helpers/PdfBuilder';
import { logError } from '../helpers/BrowserUtils';
import { Toast } from '@updox/ui-components';

/**
 * PDF Creation pre-step:
 * A user has the option to select the text, the attachment, or both of an individual message.
 * This function provides the in common information that is needed to render a message whether
 * it the text, attachment, or both are picked for the export.
 *
 * @param message The individual message object
 * @param users The redux users object
 * @returns {{senderName: string, timeSent: string, id: *}}
 */
const populateCommon = (message, users) => {
  const sender = message.senderId ? users[message.senderId] : {};
  const senderName = `${sender.firstName || ''} ${sender.lastName || ''}`;
  const timeSent = message.timeSent ? moment(message.timeSent).format('LLL') : '';

  return {
    id: message.id,
    senderName,
    timeSent,
  };
};

/**
 * PDF Creation pre-step:
 * A user has the option to select the text, the attachment, or both of an individual message.
 * This function provides the information that is needed to render the text of a message only.
 * This needs to be combined with populateCommon to render all the necessary information.
 *
 * @param message The individual message object
 * @returns {{content: (*|string)}}
 */
const populateMessage = message => ({
  content: message.content || '',
});

/**
 * PDF Creation pre-step:
 * A user has the option to select the text, the attachment, or both of an individual message.
 * This function provides the information that is needed to render the attachment of a message only.
 * This needs to be combined with populateCommon to render all the necessary information.
 *
 * @param attachment The individual message object
 * @returns {{attachment: {displayUrl: *, fileName: *, mediaType: *}}}
 */
const populateAttachment = attachment => ({
  attachment: {
    displayUrl: attachment.displayUrl,
    fileName: attachment.fileName,
    mediaType: attachment.mediaType,
  },
});

/**
 * Builds a PDF document from the selected messages and attachments stored in the
 * selectedMessages reducer, then sends the generated PDF to the current user's
 * Updox Inbox.
 *
 * @param action format of the action:
 * {
 *   type: types.PDF_EXPORT_MESSAGES,
 *   payload: chatId,
 * }
 */
export function* buildAndSendPdfExport(action) {
  const chatId = action.payload;

  // Sets the status of the PDF reducer to 'PENDING' which triggers the modal.
  yield put(startExport(chatId));

  const {
    attachments, chat, identityId, messages, selected, users,
  } = yield select(state => ({
    attachments: state.attachments,
    chat: state.chats[chatId],
    identityId: state.identity.id,
    messages: state.messages,
    selected: state.selectedMessages[chatId],
    users: state.users,
  }));

  let toExport = {};

  // Populates the export list with selected message text
  if (selected && !isEmpty(selected)) {
    if (selected[MESSAGE] && !isEmpty(selected[MESSAGE])) {
      selected[MESSAGE].forEach((messageId) => {
        const message = messages[messageId];
        toExport = mergeDeepRight(toExport, {
          [messageId]: {
            ...populateCommon(message, users),
            ...populateMessage(message),
          },
        });
      });
    }

    // Populates the export list with selected attachments
    if (selected[ATTACHMENT] && !isEmpty(selected[ATTACHMENT])) {
      selected[ATTACHMENT].forEach((attachmentId) => {
        const attachment = attachments[attachmentId];
        const { messageId } = attachment;
        toExport = mergeDeepRight(toExport, {
          [messageId]: {
            ...populateCommon(messages[messageId], users),
            ...populateAttachment(attachment),
          },
        });
      });
    }
  }

  // Orders the list of to be export messages based on message ID.
  sort(toExport);

  const chatName = chat.name || '';
  const messageIds = keys(messages);
  const requester = users[identityId];
  const requesterName = `${requester.firstName || ''} ${requester.lastName || ''}`;
  const requestDate = moment().format('LLL');

  // Builds the react-pdf document model
  const builder = new PdfBuilder();
  const pdf = builder.buildPdfExport({
    chatName, messageIds, toExport, requestDate, requesterName,
  });

  // Puts the build document model into redux
  yield put(finishExport(chatId, pdf));

  // Sends the built PDF to the Updox Inbox
  return yield put(sendExportToInbox(chatId));
}

/**
 * Sends the built pdf of the current chat to the Inbox.
 *
 * @param action format of the action:
 * {
 *   [chatId]: {
 *     status: PENDING_SUFFIX,
 *   },
 * }
 */
export function* sendPdfExport(action) {

  const chatId = action.payload;

  const { attachments, pdfObj, selectedAttachments, selectedMessages } =
    yield select(state => ({
      attachments: state.attachments,
      pdfObj: state.pdf[chatId],
      selectedAttachments: state.selectedMessages[chatId].ATTACHMENT || [],
      selectedMessages: state.selectedMessages[chatId].MESSAGE || [],
    }));
  const selectedCount = selectedAttachments.length + selectedMessages.length;

  // If there is nothing to send, do nothing
  if (!pdfObj || isEmpty(pdfObj) || !pdfObj.pdf || isEmpty(pdfObj.pdf)) {
    return null;
  }

  // Attachments IDs may be a UUID generated locally and not the actual ID of the attachment in the DB.
  // Attempt to look up the persisted id (_id), if it doesn't exist default back to id.
  let persistedAttachmentIds = [];
  selectedAttachments.forEach((selectedAttachmentId) => {
    const attachment = attachments[selectedAttachmentId];

    const id = attachment._id || attachment.id;
    const idInt = parseInt(id, 10);

    if (!isNaN(id)) {
      persistedAttachmentIds.push(idInt);
    } else {
      logError(`Attachment ID [${id}] is not an integer as expected.`);
    }
  });

  Toast.toast(`${SEND_TO_INBOX_SENDING_TOAST_PREFIX} ${selectedCount} ${selectedCount === 1 ? SEND_TO_INBOX_SENDING_TOAST_SINGULAR_ITEM : SEND_TO_INBOX_SENDING_TOAST_PLURAL_ITEM} ${SEND_TO_INBOX_SENDING_TOAST_SUFFIX}`);

  return yield PdfBuilder.sendPdfToInbox(chatId, pdfObj.pdf, persistedAttachmentIds);
}
